import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import {
  Ban,
  BarChart3,
  Users,
  Bot,
  Filter,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  Globe,
  TrendingUp,
  Target,
  MousePointer,
  Calendar,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Layers,
  Map,
  Activity,
  Radio,
  Shield,
  AlertTriangle,
  Zap,
  Eye,
  MousePointerClick
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  Treemap,
  FunnelChart,
  Funnel,
  LabelList
} from "recharts";
import LiveAnalytics from "@/components/LiveAnalytics";
import { PageHeader } from "@/components/shell/PageHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country code to name mapping for common countries
const COUNTRY_COORDS = {
  "US": { name: "United States", lat: 39.8, lng: -98.5 },
  "GB": { name: "United Kingdom", lat: 54.0, lng: -2.0 },
  "DE": { name: "Germany", lat: 51.1, lng: 10.4 },
  "FR": { name: "France", lat: 46.2, lng: 2.2 },
  "CA": { name: "Canada", lat: 56.1, lng: -106.3 },
  "AU": { name: "Australia", lat: -25.2, lng: 133.7 },
  "JP": { name: "Japan", lat: 36.2, lng: 138.2 },
  "IN": { name: "India", lat: 20.5, lng: 78.9 },
  "BR": { name: "Brazil", lat: -14.2, lng: -51.9 },
  "CN": { name: "China", lat: 35.8, lng: 104.1 },
  "RU": { name: "Russia", lat: 61.5, lng: 105.3 },
  "MX": { name: "Mexico", lat: 23.6, lng: -102.5 },
  "KR": { name: "South Korea", lat: 35.9, lng: 127.7 },
  "IT": { name: "Italy", lat: 41.8, lng: 12.5 },
  "ES": { name: "Spain", lat: 40.4, lng: -3.7 },
  "NL": { name: "Netherlands", lat: 52.1, lng: 5.2 },
  "PL": { name: "Poland", lat: 51.9, lng: 19.1 },
  "Unknown": { name: "Unknown", lat: 0, lng: 0 }
};

const Insights = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [advancedStats, setAdvancedStats] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("ALL");
  const [period, setPeriod] = useState("daily");
  const [days, setDays] = useState(7);
  const [activeTab, setActiveTab] = useState("live");
  const [exporting, setExporting] = useState(false);
  const [threatData, setThreatData] = useState(null);
  const [geoHeatmap, setGeoHeatmap] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeBlocks, setActiveBlocks] = useState(null);
  const [perLinkData, setPerLinkData] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 20, status_filter: filter }
      });
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  }, [token, page, filter]);

  const blockVisitor = async (ip, email) => {
    if (!window.confirm(`Block this visitor permanently?\n\nIP: ${ip}${email ? '\nEmail: ' + email : ''}\n\nThey will see a "Link Expired" page on future visits.`)) return;
    try {
      await axios.post(`${API}/user/ip`, { ip, action: "blacklist" }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (email) {
        await axios.post(`${API}/user/block-email`, { email }, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
      alert(`Visitor blocked successfully!\nIP: ${ip}${email ? '\nEmail: ' + email : ''}`);
      fetchLogs();
    } catch (error) {
      alert("Failed to block visitor: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  const fetchAdvancedStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/advanced`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period, days }
      });
      setAdvancedStats(response.data);
    } catch (error) {
      console.error("Error fetching advanced stats:", error);
    }
  }, [token, period, days]);

  const fetchConversions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/conversions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days }
      });
      setConversions(response.data);
    } catch (error) {
      console.error("Error fetching conversions:", error);
    }
  }, [token, days]);

  const fetchThreatIntelligence = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/threat-intelligence`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days }
      });
      setThreatData(response.data);
    } catch (error) {
      console.error("Error fetching threat intelligence:", error);
    }
  }, [token, days]);

  const fetchGeoHeatmap = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/geo-heatmap`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days }
      });
      setGeoHeatmap(response.data);
    } catch (error) {
      console.error("Error fetching geo heatmap:", error);
    }
  }, [token, days]);

  const fetchActiveBlocks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/active-blocks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveBlocks(response.data);
    } catch (error) {
      console.error("Error fetching active blocks:", error);
    }
  }, [token]);

  const fetchPerLinkData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/click-analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days }
      });
      setPerLinkData(response.data);
    } catch (error) {
      console.error("Error fetching per-link data:", error);
    }
  }, [token, days]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLogs(), fetchStats(), fetchAdvancedStats(), fetchConversions(), fetchThreatIntelligence(), fetchGeoHeatmap(), fetchActiveBlocks(), fetchPerLinkData()]);
      setLoading(false);
    };
    loadData();
    
    // Auto-refresh stats every 15 seconds
    const refreshInterval = setInterval(() => {
      fetchStats();
      fetchLogs();
    }, 15000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchLogs, fetchStats, fetchAdvancedStats, fetchConversions, fetchThreatIntelligence, fetchGeoHeatmap, fetchActiveBlocks, fetchPerLinkData]);

  useEffect(() => {
    if (!loading) {
      fetchAdvancedStats();
      fetchConversions();
      fetchThreatIntelligence();
      fetchGeoHeatmap();
      fetchActiveBlocks();
      fetchPerLinkData();
    }
  }, [loading, fetchActiveBlocks, fetchAdvancedStats, fetchConversions, fetchGeoHeatmap, fetchPerLinkData, fetchThreatIntelligence]);

  const exportData = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/analytics/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { format, period, days }
      });
      
      const { data, filename } = response.data;
      
      if (format === "csv") {
        const blob = new Blob([data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error exporting:", error);
      alert("Export failed. Please try again.");
    }
    setExporting(false);
  };

  const COLORS = ["#06b6d4", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

  const getStatusBadge = (status) => {
    const upper = status?.toUpperCase() || "";
    if (["OK", "REDIRECTED", "LANDING PAGE"].includes(upper)) {
      return <span className="badge-success">{status}</span>;
    }
    return <span className="badge-danger">{status}</span>;
  };

  // Calculate percentage change (mock for now)
  const getChange = (current, type) => {
    const changes = { visits: 12.5, ctr: 3.2, conversion: -1.8 };
    return changes[type] || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const summary = advancedStats?.summary || {};
  const timeSeries = advancedStats?.time_series || [];
  const geoData = advancedStats?.geo_data || [];
  const deviceData = advancedStats?.devices || [];
  const domainStats = advancedStats?.domain_stats || [];
  const funnelData = conversions?.funnel || {};
  const dailyConversions = conversions?.daily || [];

  return (
    <div className="space-y-6" data-testid="insights-page">
      <PageHeader
        testId="insights"
        eyebrow="Traffic intelligence"
        icon={BarChart3}
        title="Advanced Analytics"
        subtitle="Comprehensive traffic analysis and conversion tracking across every link and campaign."
        actions={<>
          <div className="flex items-center gap-1 border border-slate-800 bg-slate-900/70 p-1">
            {["hourly", "daily", "weekly"].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all ${
                  period === p ? "bg-cyan-500 text-slate-950" : "text-slate-400 hover:text-white"
                }`}
                data-testid={`period-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="input-dark text-sm py-2"
            data-testid="days-select"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button onClick={() => exportData("csv")} disabled={exporting} className="btn-secondary flex items-center gap-2 text-sm" data-testid="export-csv">
            <Download className="w-4 h-4" />CSV
          </button>
          <button onClick={() => exportData("json")} disabled={exporting} className="btn-secondary flex items-center gap-2 text-sm" data-testid="export-json">
            <FileText className="w-4 h-4" />JSON
          </button>
          <button
            onClick={async () => {
              setRefreshing(true);
              await Promise.all([fetchLogs(), fetchStats(), fetchAdvancedStats(), fetchConversions(), fetchThreatIntelligence(), fetchGeoHeatmap(), fetchActiveBlocks(), fetchPerLinkData()]);
              setRefreshing(false);
            }}
            disabled={refreshing}
            className="btn-primary flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </>}
      />

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 bg-slate-800/30 rounded-lg p-1 w-fit overflow-x-auto" data-testid="analytics-tabs">
        {[
          { id: "live", label: "Live", icon: Radio },
          { id: "overview", label: "Overview", icon: Activity },
          { id: "traffic", label: "Traffic", icon: BarChart3 },
          { id: "geo", label: "Geographic", icon: Map },
          { id: "threats", label: "Threats", icon: Shield },
          { id: "conversions", label: "Conversions", icon: Target },
          { id: "per-link", label: "Per-Link", icon: MousePointerClick },
          { id: "logs", label: "Visitor Logs", icon: Layers }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? "bg-slate-700 text-white"
                : "text-slate-400 hover:text-white"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className={`w-4 h-4 ${tab.id === 'live' && activeTab === 'live' ? 'text-green-400 animate-pulse' : ''}`} />
            {tab.label}
            {tab.id === 'live' && <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />}
          </button>
        ))}
      </div>

      {/* Live Tab */}
      {activeTab === "live" && (
        <div className="space-y-6" data-testid="live-section">
          <div className="card-dark p-6">
            <h3 className="text-lg font-semibold text-white mb-4 font-orbitron flex items-center gap-2">
              <Radio className="w-5 h-5 text-green-400 animate-pulse" />
              Real-Time Analytics
              <span className="ml-2 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">LIVE</span>
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              Monitor visitor activity in real-time. New visitors appear instantly as they access your links.
            </p>
            <LiveAnalytics />
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="card-dark p-5 stat-card-gradient" data-testid="stat-total">
              <div className="flex items-center justify-between mb-3">
                <BarChart3 className="w-8 h-8 text-cyan-400" />
                <span className="flex items-center gap-1 text-xs text-green-400">
                  <ArrowUpRight className="w-3 h-3" />
                  {getChange(summary.total_visits, "visits")}%
                </span>
              </div>
              <p className="text-slate-400 text-sm">Total Traffic</p>
              <p className="text-3xl font-bold text-white font-orbitron">
                {summary.total_visits?.toLocaleString() || 0}
              </p>
            </div>

            <div className="card-dark p-5" style={{ background: "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.02) 100%)" }} data-testid="stat-human">
              <div className="flex items-center justify-between mb-3">
                <Users className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-slate-400 text-sm">Human Visitors</p>
              <p className="text-3xl font-bold text-green-400 font-orbitron">
                {summary.human_visits?.toLocaleString() || 0}
              </p>
            </div>

            <div className="card-dark p-5" style={{ background: "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.02) 100%)" }} data-testid="stat-bot">
              <div className="flex items-center justify-between mb-3">
                <Bot className="w-8 h-8 text-red-400" />
              </div>
              <p className="text-slate-400 text-sm">Bots Blocked</p>
              <p className="text-3xl font-bold text-red-400 font-orbitron">
                {summary.bot_visits?.toLocaleString() || 0}
              </p>
            </div>

            <div className="card-dark p-5" style={{ background: "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.02) 100%)" }} data-testid="stat-ctr">
              <div className="flex items-center justify-between mb-3">
                <MousePointer className="w-8 h-8 text-purple-400" />
                <span className="flex items-center gap-1 text-xs text-green-400">
                  <ArrowUpRight className="w-3 h-3" />
                  {getChange(summary.click_through_rate, "ctr")}%
                </span>
              </div>
              <p className="text-slate-400 text-sm">Click-Through Rate</p>
              <p className="text-3xl font-bold text-purple-400 font-orbitron">
                {summary.click_through_rate || 0}%
              </p>
            </div>

            <div className="card-dark p-5" style={{ background: "linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.02) 100%)" }} data-testid="stat-conversion">
              <div className="flex items-center justify-between mb-3">
                <Target className="w-8 h-8 text-amber-400" />
                <span className={`flex items-center gap-1 text-xs ${getChange(0, "conversion") >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {getChange(0, "conversion") >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {Math.abs(getChange(0, "conversion"))}%
                </span>
              </div>
              <p className="text-slate-400 text-sm">Conversion Rate</p>
              <p className="text-3xl font-bold text-amber-400 font-orbitron">
                {summary.conversion_rate || 0}%
              </p>
            </div>
          </div>

          {/* Main Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Traffic Over Time */}
            <div className="card-dark p-6" data-testid="traffic-timeline">
              <h3 className="text-lg font-semibold text-white mb-4 font-orbitron flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
                Traffic Over Time
              </h3>
              <div className="h-72">
                {timeSeries.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={timeSeries}>
                      <defs>
                        <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="period" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          border: "1px solid #334155",
                          borderRadius: "8px"
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="total"
                        name="Total"
                        stroke="#06b6d4"
                        fill="url(#colorTotal)"
                        strokeWidth={2}
                      />
                      <Line
                        type="monotone"
                        dataKey="human"
                        name="Human"
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="redirected"
                        name="Redirected"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    No data available for selected period
                  </div>
                )}
              </div>
            </div>

            {/* Traffic Distribution Pie */}
            <div className="card-dark p-6" data-testid="traffic-distribution">
              <h3 className="text-lg font-semibold text-white mb-4 font-orbitron flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                Traffic Distribution
              </h3>
              <div className="h-72">
                {summary.total_visits > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Human", value: summary.human_visits, fill: "#22c55e" },
                          { name: "Bot", value: summary.bot_visits, fill: "#ef4444" }
                        ]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      />
                      <Legend />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          border: "1px solid #334155",
                          borderRadius: "8px"
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Traffic Tab */}
      {activeTab === "traffic" && (
        <div className="space-y-6">
          {/* Device Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-dark p-6" data-testid="device-chart">
              <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Device Types</h3>
              <div className="h-64">
                {deviceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deviceData}
                        dataKey="count"
                        nameKey="device"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                      >
                        {deviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          border: "1px solid #334155",
                          borderRadius: "8px"
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    No device data available
                  </div>
                )}
              </div>
            </div>

            {/* Domain Performance */}
            <div className="card-dark p-6" data-testid="domain-performance">
              <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Domain Performance</h3>
              <div className="h-64">
                {domainStats.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={domainStats} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis type="number" stroke="#94a3b8" />
                      <YAxis dataKey="domain" type="category" stroke="#94a3b8" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#0f172a",
                          border: "1px solid #334155",
                          borderRadius: "8px"
                        }}
                      />
                      <Legend />
                      <Bar dataKey="visits" name="Visits" fill="#06b6d4" />
                      <Bar dataKey="redirects" name="Redirects" fill="#22c55e" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    No domain data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hourly Heatmap */}
          <div className="card-dark p-6" data-testid="hourly-heatmap">
            <h3 className="text-lg font-semibold text-white mb-4 font-orbitron flex items-center gap-2">
              <Clock className="w-5 h-5 text-cyan-400" />
              Traffic by Hour
            </h3>
            <div className="h-48">
              {stats?.hourly_stats && stats.hourly_stats.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.hourly_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="hour" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0f172a",
                        border: "1px solid #334155",
                        borderRadius: "8px"
                      }}
                    />
                    <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  No hourly data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Geographic Tab */}
      {activeTab === "geo" && (
        <div className="space-y-6">
          {/* Geographic Heatmap */}
          <div className="card-dark p-6" data-testid="geo-heatmap">
            <h3 className="text-lg font-semibold text-white mb-4 font-orbitron flex items-center gap-2">
              <Globe className="w-5 h-5 text-cyan-400" />
              Geographic Distribution
            </h3>
            
            {/* Country Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              {(geoHeatmap?.countries || geoData).slice(0, 10).map((country, idx) => {
                const allCountries = geoHeatmap?.countries || geoData;
                const maxCount = Math.max(...allCountries.map(g => g.total || g.count || 0), 1);
                const total = country.total || country.count || 0;
                const human = country.human || 0;
                const blocked = country.blocked || 0;
                const intensity = (total / maxCount) * 100;
                
                return (
                  <div
                    key={country.country}
                    className="p-4 rounded-lg border border-slate-700/50 hover:border-cyan-500/30 transition-all"
                    style={{
                      background: `linear-gradient(135deg, rgba(6, 182, 212, ${intensity / 500}) 0%, rgba(6, 182, 212, ${intensity / 1000}) 100%)`
                    }}
                    data-testid={`geo-country-${idx}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">{idx + 1}.</span>
                      <span className="text-white font-medium truncate">{country.country}</span>
                    </div>
                    <p className="text-2xl font-bold text-cyan-400">{total.toLocaleString()}</p>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="text-green-400">OK: {human}</span>
                      <span className="text-red-400">Blocked: {blocked}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Country Distribution Bar Chart */}
            <div className="h-64">
              {(geoHeatmap?.countries || geoData).length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(geoHeatmap?.countries || geoData).slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" stroke="#94a3b8" />
                    <YAxis dataKey="country" type="category" stroke="#94a3b8" width={80} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                    />
                    <Legend />
                    <Bar dataKey={geoHeatmap ? "human" : "human"} name="Human" fill="#22c55e" stackId="stack" />
                    <Bar dataKey="blocked" name="Blocked" fill="#ef4444" stackId="stack" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  No geographic data available
                </div>
              )}
            </div>
          </div>

          {/* City-Level Heat Map */}
          {geoHeatmap?.cities && geoHeatmap.cities.length > 0 && (
            <div className="card-dark p-6" data-testid="city-heatmap">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Map className="w-5 h-5 text-purple-400" />
                City-Level Traffic Heat Map
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* City Treemap */}
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                      data={geoHeatmap.cities.slice(0, 20).map((c, i) => ({
                        name: `${c.city || 'Unknown'}, ${c.country || '??'}`,
                        size: c.total,
                        human: c.human,
                        blocked: c.blocked
                      }))}
                      dataKey="size"
                      aspectRatio={4 / 3}
                      stroke="#1e293b"
                      content={({ x, y, width, height, name, size }) => {
                        if (width < 30 || height < 20) return null;
                        return (
                          <g>
                            <rect x={x} y={y} width={width} height={height} fill="#06b6d4" fillOpacity={0.15 + (size / Math.max(...geoHeatmap.cities.map(c => c.total))) * 0.6} stroke="#1e293b" strokeWidth={2} rx={4} />
                            {width > 60 && height > 30 && (
                              <>
                                <text x={x + 6} y={y + 16} fill="#e2e8f0" fontSize={11} fontWeight="bold">{(name || '').substring(0, Math.floor(width / 7))}</text>
                                <text x={x + 6} y={y + 30} fill="#06b6d4" fontSize={10}>{size}</text>
                              </>
                            )}
                          </g>
                        );
                      }}
                    />
                  </ResponsiveContainer>
                </div>

                {/* City Table */}
                <div className="overflow-y-auto max-h-72">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-900">
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-2 text-slate-400 text-xs">City</th>
                        <th className="text-left py-2 text-slate-400 text-xs">Country</th>
                        <th className="text-right py-2 text-slate-400 text-xs">Total</th>
                        <th className="text-right py-2 text-slate-400 text-xs">Human</th>
                        <th className="text-right py-2 text-slate-400 text-xs">Blocked</th>
                        <th className="text-right py-2 text-slate-400 text-xs">Visitors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoHeatmap.cities.slice(0, 30).map((city, i) => (
                        <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-2 text-white text-xs">{city.city || 'Unknown'}</td>
                          <td className="py-2 text-slate-400 text-xs">{city.country}</td>
                          <td className="py-2 text-right text-cyan-400 font-mono text-xs">{city.total}</td>
                          <td className="py-2 text-right text-green-400 font-mono text-xs">{city.human}</td>
                          <td className="py-2 text-right text-red-400 font-mono text-xs">{city.blocked}</td>
                          <td className="py-2 text-right text-slate-300 font-mono text-xs">{city.unique_visitors}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Country Stats Table */}
          <div className="card-dark p-6" data-testid="country-table">
            <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Country Details</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-800">
                    <th className="pb-3 font-medium">#</th>
                    <th className="pb-3 font-medium">Country</th>
                    <th className="pb-3 font-medium text-right">Total</th>
                    <th className="pb-3 font-medium text-right">Human</th>
                    <th className="pb-3 font-medium text-right">Blocked</th>
                    <th className="pb-3 font-medium text-right">Block Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {(geoHeatmap?.countries || geoData).map((country, idx) => {
                    const total = country.total || country.count || 0;
                    const human = country.human || 0;
                    const blocked = country.blocked || 0;
                    return (
                      <tr key={country.country} className="hover:bg-slate-800/50">
                        <td className="py-3 text-slate-500">{idx + 1}</td>
                        <td className="py-3 text-white font-medium">{country.country}</td>
                        <td className="py-3 text-right text-cyan-400">{total.toLocaleString()}</td>
                        <td className="py-3 text-right text-green-400">{human.toLocaleString()}</td>
                        <td className="py-3 text-right text-red-400">{blocked.toLocaleString()}</td>
                        <td className="py-3 text-right">
                          <span className={`text-xs font-mono ${total > 0 && blocked / total > 0.5 ? 'text-red-400' : 'text-slate-400'}`}>
                            {total > 0 ? ((blocked / total) * 100).toFixed(1) : 0}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(geoHeatmap?.countries || geoData).length === 0 && (
                <div className="text-center py-8 text-slate-500">No geographic data available</div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Threats Tab - Bot Threat Intelligence Dashboard */}
      {activeTab === "threats" && (
        <div className="space-y-6" data-testid="threats-section">
          {/* Threat Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-dark p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-red-400" />
                <span className="text-xs text-slate-400">Total Blocked</span>
              </div>
              <div className="text-2xl font-bold text-white">{threatData?.summary?.total_blocked || 0}</div>
              <div className="text-xs text-slate-500 mt-1">in last {days} days</div>
            </div>
            <div className="card-dark p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-slate-400">Block Rate</span>
              </div>
              <div className="text-2xl font-bold text-white">{threatData?.summary?.block_rate || 0}%</div>
              <div className="text-xs text-slate-500 mt-1">of all traffic</div>
            </div>
            <div className="card-dark p-5">
              <div className="flex items-center gap-2 mb-2">
                <Zap className={`w-4 h-4 ${
                  threatData?.summary?.threat_level === 'CRITICAL' ? 'text-red-500' :
                  threatData?.summary?.threat_level === 'HIGH' ? 'text-orange-400' :
                  threatData?.summary?.threat_level === 'MEDIUM' ? 'text-amber-400' : 'text-green-400'
                }`} />
                <span className="text-xs text-slate-400">Threat Level</span>
              </div>
              <div className={`text-2xl font-bold ${
                threatData?.summary?.threat_level === 'CRITICAL' ? 'text-red-500' :
                threatData?.summary?.threat_level === 'HIGH' ? 'text-orange-400' :
                threatData?.summary?.threat_level === 'MEDIUM' ? 'text-amber-400' : 'text-green-400'
              }`}>{threatData?.summary?.threat_level || 'LOW'}</div>
              <div className="text-xs text-slate-500 mt-1">current assessment</div>
            </div>
            <div className="card-dark p-5">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-slate-400">Hostile ISPs</span>
              </div>
              <div className="text-2xl font-bold text-white">{threatData?.summary?.unique_blocked_isps || 0}</div>
              <div className="text-xs text-slate-500 mt-1">unique sources</div>
            </div>
          </div>

          {/* Blocked Traffic Timeline */}
          <div className="card-dark p-6" data-testid="blocked-timeline">
            <h3 className="text-lg font-semibold text-white mb-4">Blocked Traffic Over Time</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={threatData?.blocked_timeline || []}>
                  <defs>
                    <linearGradient id="blockedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", color: "#fff" }}
                  />
                  <Area type="monotone" dataKey="blocked" stroke="#ef4444" fill="url(#blockedGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Block Reasons Breakdown */}
            <div className="card-dark p-6" data-testid="block-reasons">
              <h3 className="text-lg font-semibold text-white mb-4">Block Reasons</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={(threatData?.block_reasons || []).map(r => ({
                        name: r.reason?.replace('BLOCKED:', '').replace('BLOCKED_', '') || 'Unknown',
                        value: r.count
                      }))}
                      cx="50%" cy="50%"
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {(threatData?.block_reasons || []).map((_, i) => (
                        <Cell key={i} fill={['#ef4444', '#f97316', '#eab308', '#6366f1', '#ec4899', '#14b8a6', '#8b5cf6'][i % 7]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", color: "#fff" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Blocked Countries */}
            <div className="card-dark p-6" data-testid="blocked-countries">
              <h3 className="text-lg font-semibold text-white mb-4">Top Blocked Countries</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={threatData?.blocked_countries || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis type="number" stroke="#64748b" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="country" type="category" width={80} stroke="#64748b" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", color: "#fff" }} />
                    <Bar dataKey="count" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Hostile ISPs Table */}
          <div className="card-dark p-6" data-testid="hostile-isps">
            <h3 className="text-lg font-semibold text-white mb-4">Top Hostile ISPs</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 text-slate-400 font-medium">ISP</th>
                    <th className="text-center py-3 text-slate-400 font-medium">Blocked</th>
                    <th className="text-center py-3 text-slate-400 font-medium">Unique IPs</th>
                    <th className="text-right py-3 text-slate-400 font-medium">Threat Score</th>
                  </tr>
                </thead>
                <tbody>
                  {(threatData?.top_isps || []).map((isp, i) => {
                    const score = Math.min(100, Math.round((isp.count / Math.max(1, threatData?.summary?.total_blocked)) * 100 + isp.unique_ips * 5));
                    return (
                      <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                        <td className="py-3 text-white font-medium">{isp.isp || 'Unknown'}</td>
                        <td className="py-3 text-center text-red-400 font-mono">{isp.count}</td>
                        <td className="py-3 text-center text-slate-300 font-mono">{isp.unique_ips}</td>
                        <td className="py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${score > 70 ? 'bg-red-500' : score > 40 ? 'bg-amber-400' : 'bg-green-400'}`} style={{ width: `${score}%` }} />
                            </div>
                            <span className={`text-xs font-mono ${score > 70 ? 'text-red-400' : score > 40 ? 'text-amber-400' : 'text-green-400'}`}>{score}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(!threatData?.top_isps || threatData.top_isps.length === 0) && (
                <div className="text-center py-8 text-slate-500">No blocked traffic detected in the last {days} days</div>
              )}
            </div>
          </div>

          {/* Top Blocked IPs (Repeat Offenders) */}
          <div className="card-dark p-6" data-testid="repeat-offenders">
            <h3 className="text-lg font-semibold text-white mb-4">Repeat Offenders (Top Blocked IPs)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 text-slate-400 font-medium">IP Address</th>
                    <th className="text-left py-3 text-slate-400 font-medium">ISP</th>
                    <th className="text-left py-3 text-slate-400 font-medium">Country</th>
                    <th className="text-left py-3 text-slate-400 font-medium">ASN</th>
                    <th className="text-center py-3 text-slate-400 font-medium">Hits</th>
                    <th className="text-left py-3 text-slate-400 font-medium">Reasons</th>
                    <th className="text-right py-3 text-slate-400 font-medium">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(threatData?.top_blocked_ips || []).slice(0, 15).map((entry, i) => (
                    <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="py-2 text-red-400 font-mono text-xs">{entry.ip}</td>
                      <td className="py-2 text-slate-300 text-xs">{(entry.isp || '').substring(0, 25)}</td>
                      <td className="py-2 text-slate-300 text-xs">{entry.country}</td>
                      <td className="py-2 text-slate-400 font-mono text-xs">{entry.asn}</td>
                      <td className="py-2 text-center text-red-400 font-mono font-bold">{entry.count}</td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-1">
                          {(entry.reasons || []).map((r, j) => (
                            <span key={j} className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded">
                              {r?.replace('BLOCKED:', '').replace('BLOCKED_', '')}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2 text-right text-slate-500 text-xs">{entry.last_seen?.substring(0, 16)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!threatData?.top_blocked_ips || threatData.top_blocked_ips.length === 0) && (
                <div className="text-center py-8 text-slate-500">No repeat offenders detected</div>
              )}
            </div>
          </div>

          {/* Top Blocked ASNs */}
          <div className="card-dark p-6" data-testid="blocked-asns">
            <h3 className="text-lg font-semibold text-white mb-4">Top Blocked ASNs</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(threatData?.top_asns || []).slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="asn" stroke="#64748b" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", color: "#fff" }}
                    formatter={(val, name, props) => [val, `${props.payload.asn} (${props.payload.isp})`]}
                  />
                  <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Active Blocks (Last 7 Days) */}
          <div className="card-dark p-6" data-testid="active-blocks-table">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Active Security Blocks (Last 7 Days)</h3>
              <span className="text-xs text-slate-500">
                {activeBlocks?.total_blocked_all_time?.toLocaleString() || 0} blocked all time
              </span>
            </div>
            {(activeBlocks?.blocks || []).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 text-slate-400 font-medium">Block Type</th>
                      <th className="text-left py-3 text-slate-400 font-medium">Description</th>
                      <th className="text-right py-3 text-slate-400 font-medium">Count (7d)</th>
                      <th className="text-right py-3 text-slate-400 font-medium">Unique IPs</th>
                      <th className="text-right py-3 text-slate-400 font-medium">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activeBlocks?.blocks || []).map((b, i) => (
                      <tr key={b.block_type} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                        <td className="py-3">
                          <span className="px-2 py-1 rounded text-xs font-mono bg-red-500/10 text-red-400 border border-red-500/20">
                            {b.block_type?.replace("BLOCKED:", "")}
                          </span>
                        </td>
                        <td className="py-3 text-slate-400 text-xs max-w-xs">{b.description}</td>
                        <td className="py-3 text-right text-white font-semibold">{b.count_7d}</td>
                        <td className="py-3 text-right text-slate-300">{b.unique_ips}</td>
                        <td className="py-3 text-right text-slate-500 text-xs">
                          {b.last_seen ? new Date(b.last_seen).toLocaleDateString() : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500 text-sm">
                No blocks recorded in the last 7 days
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === "conversions" && (
        <div className="space-y-6">
          {/* Conversion Funnel */}
          <div className="card-dark p-6" data-testid="conversion-funnel">
            <h3 className="text-lg font-semibold text-white mb-6 font-orbitron flex items-center gap-2">
              <Target className="w-5 h-5 text-cyan-400" />
              Conversion Funnel
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Funnel Steps */}
              {[
                { label: "Total Visits", value: funnelData.total_visits, color: "cyan", icon: BarChart3 },
                { label: "Bot Filtered", value: funnelData.bot_filtered, color: "red", icon: Bot, negative: true },
                { label: "Human Visits", value: funnelData.human_visits, color: "green", icon: Users },
                { label: "Landing Views", value: funnelData.landing_views, color: "purple", icon: Layers },
                { label: "Conversions", value: funnelData.conversions, color: "amber", icon: Target }
              ].map((step, idx) => (
                <div
                  key={step.label}
                  className={`relative p-4 rounded-lg border ${
                    step.negative ? "border-red-500/30 bg-red-500/5" : "border-slate-700/50"
                  }`}
                  data-testid={`funnel-step-${idx}`}
                >
                  <step.icon className={`w-6 h-6 text-${step.color}-400 mb-2`} />
                  <p className="text-slate-400 text-sm">{step.label}</p>
                  <p className={`text-2xl font-bold text-${step.color}-400 font-orbitron`}>
                    {(step.value || 0).toLocaleString()}
                  </p>
                  {idx < 4 && (
                    <div className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                      <ChevronRight className="w-6 h-6 text-slate-600" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Conversion Rates */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-green-400 text-sm mb-1">Human Traffic Rate</p>
                <p className="text-3xl font-bold text-green-400">{conversions?.human_rate || 0}%</p>
                <p className="text-xs text-slate-400 mt-1">Visitors that passed bot detection</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-amber-400 text-sm mb-1">Conversion Rate</p>
                <p className="text-3xl font-bold text-amber-400">{conversions?.conversion_rate || 0}%</p>
                <p className="text-xs text-slate-400 mt-1">Human visitors who completed redirect</p>
              </div>
            </div>
          </div>

          {/* Daily Conversions Chart */}
          <div className="card-dark p-6" data-testid="daily-conversions">
            <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Daily Conversion Tracking</h3>
            <div className="h-72">
              {dailyConversions.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={dailyConversions}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" stroke="#94a3b8" />
                    <YAxis yAxisId="right" orientation="right" stroke="#f59e0b" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0f172a",
                        border: "1px solid #334155",
                        borderRadius: "8px"
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="visits" name="Visits" fill="#06b6d4" />
                    <Bar yAxisId="left" dataKey="converted" name="Converted" fill="#22c55e" />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="rate"
                      name="Conversion %"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={{ fill: "#f59e0b" }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  No conversion data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Per-Link Tab */}
      {activeTab === "per-link" && (
        <div className="space-y-6" data-testid="per-link-section">
          <div className="card-dark p-6" data-testid="per-link-table">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white font-orbitron">Per-Link Click Breakdown</h3>
              <p className="text-xs text-slate-500 mt-1">Top links by redirected clicks</p>
            </div>
            {(perLinkData?.top_links || []).length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-800">
                      <th className="pb-3 font-medium">Domain</th>
                      <th className="pb-3 font-medium">Path</th>
                      <th className="pb-3 font-medium text-right">Clicks</th>
                      <th className="pb-3 font-medium text-right">Unique Visitors</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {(perLinkData?.top_links || []).map((l, i) => (
                      <tr key={i} className="hover:bg-slate-800/50">
                        <td className="py-3 text-cyan-400 text-xs font-mono max-w-[200px] truncate">{l.domain}</td>
                        <td className="py-3 text-slate-300 text-xs font-mono max-w-[300px] truncate" title={l.path}>
                          {l.path === "(root)" ? "/" : `/${l.path}`}
                        </td>
                        <td className="py-3 text-right">
                          <span className="text-green-400 font-semibold">{l.clicks}</span>
                        </td>
                        <td className="py-3 text-right text-slate-400">{l.unique_visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No per-link click data yet. Clicks will appear here after visitors use your redirect links.
              </div>
            )}
          </div>

          {/* Click Distribution Chart */}
          {(perLinkData?.top_links || []).length > 0 && (
            <div className="card-dark p-6" data-testid="link-distribution-chart">
              <h3 className="text-lg font-semibold text-white mb-4 font-orbitron">Click Distribution</h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(perLinkData?.top_links || []).slice(0, 10).map(l => ({
                    path: l.path?.length > 25 ? l.path.slice(0, 25) + "..." : l.path || "(empty)",
                    clicks: l.clicks,
                    unique: l.unique_visitors
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="path" tick={{ fill: "#94a3b8", fontSize: 9, angle: -20 }}
                      stroke="#334155" height={60} />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} stroke="#334155" />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px" }}
                    />
                    <Legend />
                    <Bar dataKey="clicks" name="Clicks" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="unique" name="Unique Visitors" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Visitor Logs Tab */}
      {activeTab === "logs" && (
        <div className="card-dark p-6" data-testid="logs-table">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white font-orbitron">Visitor Logs</h3>
            
            {/* Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setPage(1);
                }}
                className="input-dark text-sm py-1"
                data-testid="filter-select"
              >
                <option value="ALL">All Traffic</option>
                <option value="HUMAN">Human Only</option>
                <option value="BOT">Bots Only</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-800">
                  <th className="pb-3 font-medium">Timestamp</th>
                  <th className="pb-3 font-medium">IP</th>
                  <th className="pb-3 font-medium">Email</th>
                  <th className="pb-3 font-medium">Country</th>
                  <th className="pb-3 font-medium">ISP</th>
                  <th className="pb-3 font-medium">ASN</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-slate-500">
                      No visitor logs found. Click a redirect link to generate traffic data.
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => {
                    // Format timestamp from ISO to dd-mm-yyyy hh:mm:ss
                    const formatTimestamp = (ts) => {
                      if (!ts) return '-';
                      try {
                        const date = new Date(ts);
                        return date.toLocaleString('en-GB', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', second: '2-digit'
                        }).replace(',', '');
                      } catch { return ts; }
                    };
                    return (
                    <tr key={index} className="hover:bg-slate-800/50">
                      <td className="py-3 font-mono text-xs text-slate-400">{formatTimestamp(log.timestamp)}</td>
                      <td className="py-3 font-mono text-xs text-slate-300">{log.ip}</td>
                      <td className="py-3 font-mono text-xs">
                        {log.email ? (
                          <span className="text-green-400">{log.email}</span>
                        ) : (
                          <span className="text-slate-600">-</span>
                        )}
                      </td>
                      <td className="py-3 text-slate-300">{log.country}</td>
                      <td className="py-3 text-slate-400 text-xs truncate max-w-[150px]">{log.isp}</td>
                      <td className="py-3 font-mono text-xs text-slate-500">{log.asn || "-"}</td>
                      <td className="py-3">{getStatusBadge(log.status)}</td>
                      <td className="py-3">
                        <button
                          onClick={() => blockVisitor(log.ip, log.email)}
                          className="flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/30 transition-colors"
                          title="Block this visitor permanently"
                        >
                          <Ban className="w-3 h-3" />
                          Block
                        </button>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
              <p className="text-sm text-slate-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-ghost p-2 disabled:opacity-50"
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-ghost p-2 disabled:opacity-50"
                  data-testid="next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Insights;
